import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Copy, ArrowLeft, UsersThree, CurrencyDollar, Share } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Footer } from "@/components/Footer";

export default function Referral() {
  const { isAuthenticated, loading } = useAuth();
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      nav("/login", { state: { from: "/app/referral" } });
      return;
    }
    api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
  }, [isAuthenticated, loading, nav]);

  const link = data
    ? `${window.location.origin}/signup?ref=${data.referral_code}`
    : "";

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "LoadWise",
          text: "Smarter Loads. Bigger Profits. Screen your loads for profitability — get 3 free/day.",
          url: link,
        });
      } catch (e) { /* cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div className="min-h-screen bg-[#0a192f] text-white landing-root">
      <div className="ticker-line" />
      <header className="max-w-[900px] mx-auto px-5 md:px-8 py-5">
        <Link to="/app" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white w-fit">
          <ArrowLeft size={14} weight="bold" className="flip-x" />
          Back to calculator
        </Link>
      </header>
      <main className="max-w-[900px] mx-auto px-5 md:px-8 pb-12">
        <div className="kicker"><span className="dot" />Refer & Earn</div>
        <h1 className="h-section mt-4 text-white">
          Earn <span className="neon-text italic">$9 credit</span> for every friend who subscribes.
        </h1>
        <p className="mt-3 text-slate-400 text-base max-w-lg">
          Share your unique link. When someone signs up and becomes a paying customer, you get $9 credited to your account balance.
        </p>

        {!data ? (
          <div className="mt-8 h-40 glass rounded-2xl grid place-items-center text-slate-500 text-sm">
            Loading…
          </div>
        ) : (
          <>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <div data-testid="ref-invited" className="glass rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/25 grid place-items-center text-emerald-300">
                  <UsersThree size={16} weight="duotone" />
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-[0.14em] text-slate-400">Invited</p>
                <p className="tabular text-3xl font-semibold text-white mt-1">{data.invited_count}</p>
              </div>
              <div data-testid="ref-paid" className="glass rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/25 grid place-items-center text-emerald-300">
                  <UsersThree size={16} weight="duotone" />
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-[0.14em] text-slate-400">Paid conversions</p>
                <p className="tabular text-3xl font-semibold text-white mt-1">{data.paid_conversions}</p>
              </div>
              <div data-testid="ref-credit" className="glass rounded-2xl p-5 neon-border">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-400/40 grid place-items-center text-emerald-300">
                  <CurrencyDollar size={16} weight="duotone" />
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-[0.14em] text-slate-400">Total credit</p>
                <p className="tabular text-3xl font-semibold neon-text mt-1">${data.total_credit_usd}</p>
              </div>
            </div>

            <div className="mt-6 glass-strong rounded-2xl p-5 md:p-6">
              <p className="text-[10px] uppercase tracking-[0.14em] text-emerald-300 font-medium">Your link</p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="ref-link"
                  readOnly
                  value={link}
                  className="flex-1 rounded-lg bg-white/[0.04] border border-white/10 text-white text-sm px-3 py-3 tabular"
                />
                <Button data-testid="ref-copy" onClick={copy} className="btn-neon rounded-lg h-11 px-4">
                  <Copy size={14} weight="bold" className="me-1.5" />
                  Copy
                </Button>
                <Button
                  onClick={share}
                  variant="outline"
                  className="rounded-lg h-11 px-4 border-white/15 text-slate-200 bg-white/[0.04]"
                >
                  <Share size={14} weight="bold" className="me-1.5" />
                  Share
                </Button>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Reward is credited to your account balance when your referral's first payment succeeds. $9 per paid signup.
              </p>
            </div>
          </>
        )}
      </main>
      <Footer />
      <Toaster position="top-right" richColors closeButton theme="dark" />
    </div>
  );
}
